<script setup lang="ts">
import { PopoverConfirm, useBreakpoints } from '@collector/shared-ui'
import { IncidentId } from '@collector/sportsapi-client-legacy'
import {
  useEventDetails,
  useParticipantResultsMaps,
} from '@desktop/views/Relation/composables'
import IncidentButton from '@desktop/views/Relation/Sports/components/RelationView/IncidentButton.vue'
import { useEventIncidentsQueue } from '@desktop/views/Relation/Sports/composables'
import {
  ConfirmingStrategy,
  EventRelatedStatusValue,
} from '@desktop/views/Relation/Sports/configuration'

import { getIncidentId, isObjectValue } from './utils'

interface Props {
  title: string
  eventRelatedStatusValues: EventRelatedStatusValue[]
}

defineProps<Props>()

// emits
const emit = defineEmits(['click'])

// composables
const eventIncidentsQueue = useEventIncidentsQueue()
const { breakpoints } = useBreakpoints()
const { homeParticipantResultsMap, awayParticipantResultsMap } =
  useParticipantResultsMaps()
const { getEventDetailValue } = useEventDetails()

// methods
function addIncidentToQueue(incidentId: IncidentId): void {
  eventIncidentsQueue.addIncident(
    {
      incident_id: incidentId,
    },
    { shouldResetPossession: true },
  )
  emit('click')
}

function getDescription(value: EventRelatedStatusValue): string | null {
  return isObjectValue(value) && value.description ? value.description : null
}

function isButtonBlocked(value: EventRelatedStatusValue): boolean {
  if (!isObjectValue(value) || !value.blockingStrategy) {
    return false
  }

  return value.blockingStrategy.isBlocked({
    homeResult: value.blockingStrategy.resultId
      ? Number(
          homeParticipantResultsMap.value[value.blockingStrategy.resultId]
            ?.value,
        )
      : undefined,
    awayResult: value.blockingStrategy.resultId
      ? Number(
          awayParticipantResultsMap.value[value.blockingStrategy.resultId]
            ?.value,
        )
      : undefined,
    getEventDetailValue,
  })
}

function getUnblockInstructions(value: EventRelatedStatusValue): string {
  if (
    !isObjectValue(value) ||
    !value.blockingStrategy ||
    !isButtonBlocked(value)
  ) {
    return ''
  }

  return value.blockingStrategy.unblockInstructions
}

function isConfirmationRequired(value: EventRelatedStatusValue): boolean {
  return isObjectValue(value) && !!value.confirmingStrategy
}

function getConfirmationDescription(value: EventRelatedStatusValue): string {
  if (!isObjectValue(value) || !value.confirmingStrategy) {
    return ''
  }

  return getMatchResultConfirmation(value.confirmingStrategy)
}

function getMatchResultConfirmation(strategy: ConfirmingStrategy): string {
  if (!strategy.resultId) {
    return ''
  }
  return strategy.getConfirmationDescription({
    home: Number(homeParticipantResultsMap.value[strategy.resultId]?.value),
    away: Number(awayParticipantResultsMap.value[strategy.resultId]?.value),
  })
}
</script>

<template>
  <div class="flex flex-col gap-y-2">
    <div class="text-sm font-bold">
      {{ title }}
    </div>
    <div class="flex flex-col gap-2">
      <div
        v-for="eventRelatedStatusValue in eventRelatedStatusValues"
        :key="getIncidentId(eventRelatedStatusValue)"
        class="flex flex-col gap-y-0.5"
      >
        <PopoverConfirm
          v-if="isConfirmationRequired(eventRelatedStatusValue)"
          cancelLabel="No"
          confirmLabel="Yes"
          @confirm="
            () => addIncidentToQueue(getIncidentId(eventRelatedStatusValue))
          "
        >
          <template #confirmText>
            <div class="w-30">
              <div>
                {{ getConfirmationDescription(eventRelatedStatusValue) }}
              </div>
            </div>
          </template>
          <template #trigger>
            <IncidentButton
              class="bg-neutral-light-8! border-neutral-light-0 justify-start px-3"
              :class="{
                'hover:bg-neutral-light-0!': !isButtonBlocked(
                  eventRelatedStatusValue,
                ),
              }"
              :incidentId="getIncidentId(eventRelatedStatusValue)"
              :size="breakpoints.sm.value ? 'lg' : 'sm'"
              width="slim"
              :disabled="isButtonBlocked(eventRelatedStatusValue)"
              :tooltip="getUnblockInstructions(eventRelatedStatusValue)"
            >
              <template #default="{ incidentName }">
                <span class="text-left font-bold">
                  {{ incidentName }}
                </span>
              </template>
            </IncidentButton>
          </template>
        </PopoverConfirm>
        <IncidentButton
          v-else
          class="bg-neutral-light-8! border-neutral-light-0 justify-start px-3"
          :class="{
            'hover:bg-neutral-light-0!': !isButtonBlocked(
              eventRelatedStatusValue,
            ),
          }"
          :incidentId="getIncidentId(eventRelatedStatusValue)"
          :size="breakpoints.sm.value ? 'lg' : 'sm'"
          width="slim"
          :disabled="isButtonBlocked(eventRelatedStatusValue)"
          :tooltip="getUnblockInstructions(eventRelatedStatusValue)"
          @click="addIncidentToQueue"
        >
          <template #default="{ incidentName }">
            <span class="text-left font-bold">
              {{ incidentName }}
            </span>
          </template>
        </IncidentButton>
        <div
          v-if="getDescription(eventRelatedStatusValue)"
          class="text-xs"
        >
          {{ getDescription(eventRelatedStatusValue) }}
        </div>
      </div>
    </div>
  </div>
</template>
